import { useEffect } from "react";
import { Icon, IconBox } from "@proda-ai/ui-kit";
import Typography from "@/ui/atoms/Typography";

import Box from "@/ui/atoms/Box";
import type { BoxProps } from "@/ui/atoms/Box";
import type {
    DealNameCheck,
    PropertyAddressCheck,
    PropertyAddressCheckInfo,
    MultipropDeal,
    MultipropPropertyInfo,
    PropertyNameCheck,
    PropertyInfoForDelete,
    NewProperty,
    ProjectInfo,
} from "@/ui/organisms/dealTypes";
import PropertyForm from "@/ui/organisms/DealForm/PropertyForm";
import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";
import Loader from "@/ui/atoms/Loader";
import ErrorMessage from "@/ui/atoms/ErrorMessage";
import PropertyList from "@/ui/organisms/DealForm/BasePropertyList";
import { EditDealInfoForm } from "@/ui/organisms/DealForm/EditDealInfoForm";
import { DeletePropertyModal } from "@/ui/organisms/DealForm/DeletePropertyModal";

export const testIds = {
    tabButton: "ViewDealDetails-tabButton",
};

export const tabs = [
    { value: "description", label: "Project Information" },
    { value: "properties", label: "Properties" },
] as const;

export type ViewDealDetailsParams = {
    useState: (params: { projectId: number; databaseId: number }) => {
        availableCoAs: Array<{
            coaId: number;
            name: string;
        }> | null;
        availableCoAsStatus: "idle" | "pending" | "resolved" | "rejected";
        currentDeal: MultipropDeal | null;
        dealIsPending: boolean;
        dealIsError: boolean;
        editingProperty: boolean;
        currentProperty: Partial<MultipropPropertyInfo> | Partial<NewProperty>;
        propertyAddressCheck: PropertyAddressCheck | null;
        configGoogleMapsState: {
            status: "idle" | "pending" | "resolved" | "rejected";
            data: {
                apiKey: string;
            } | null;
        };
        isAddPropertyPending: boolean;
        deletePropertyModalOpen: boolean;
        deleteInfo: PropertyInfoForDelete | null;
        isDeleteInfoPending: boolean;
        hasCoaLockingExtractions: boolean;
    };
    useCases: (params: { projectId: number; databaseId: number }) => {
        setDealNameCheck: (
            databaseId: number,
            dealName: string,
        ) => Promise<DealNameCheck>;
        setPropertyAddressCheck: (
            data: PropertyAddressCheckInfo,
        ) => Promise<PropertyAddressCheck>;
        setPropertyNameCheck: (
            databaseId: number,
            propertyName: string,
        ) => Promise<PropertyNameCheck>;
        onMount: ({ databaseId }: { databaseId: number }) => void;
        onUnmount: () => void;
        updateDeal: (data: ProjectInfo) => Promise<void>;
        newProperty: () => void;
        mutateNewProperty: (data: Partial<NewProperty>) => void;
        editProperty: (property: MultipropPropertyInfo) => void;
        addProperty: (data: NewProperty) => Promise<void>;
        updateProperty: (data: MultipropPropertyInfo) => Promise<void>;
        deleteProperty: (propertyId: number) => Promise<void>;
        toggleEditingProperty: () => void;
        setDeletePropertyModalStatus: (open: boolean) => void;
        handleDeletePropertyModalOpen: (propertyId: number) => void;
    };
    getCurrentDate: () => Date;
};

export type ViewDealDetailsProps = {
    projectId: number;
    databaseId: number;
    handleBackButtonClicked: () => void;
    activeDetailsTab: "properties" | "description";
    setActiveDetailsTab: (tabName: "properties" | "description") => void;
} & Omit<BoxProps, "children">;

export const createViewDealDetails = ({
    useState,
    useCases,
    getCurrentDate,
}: ViewDealDetailsParams) => {
    const ViewDealDetails = ({
        projectId,
        databaseId,
        handleBackButtonClicked,
        activeDetailsTab,
        setActiveDetailsTab,
    }: ViewDealDetailsProps) => {
        const {
            currentDeal,
            dealIsPending,
            dealIsError,
            editingProperty,
            currentProperty,
            propertyAddressCheck,
            configGoogleMapsState,
            availableCoAs,
            availableCoAsStatus,
            isAddPropertyPending,
            deletePropertyModalOpen,
            deleteInfo,
            isDeleteInfoPending,
            hasCoaLockingExtractions,
        } = useState({ projectId, databaseId });
        const {
            addProperty,
            updateDeal,
            newProperty,
            mutateNewProperty,
            editProperty,
            updateProperty,
            deleteProperty,
            setDealNameCheck,
            setPropertyAddressCheck,
            setPropertyNameCheck,
            toggleEditingProperty,
            onMount,
            onUnmount,
            setDeletePropertyModalStatus,
            handleDeletePropertyModalOpen,
        } = useCases({ projectId, databaseId });

        useEffect(() => {
            onMount({ databaseId });

            return onUnmount;
        }, [onMount, databaseId, onUnmount]);

        if (dealIsError) {
            return (
                <LayoutFillParentCentered
                    sx={{
                        backgroundColor: "var(--proda-white)",
                    }}
                >
                    <ErrorMessage>Error loading project details</ErrorMessage>
                </LayoutFillParentCentered>
            );
        }

        if (dealIsPending || currentDeal === null) {
            return (
                <LayoutFillParentCentered
                    sx={{
                        backgroundColor: "var(--proda-white)",
                    }}
                >
                    <Loader />
                </LayoutFillParentCentered>
            );
        }

        const handleDeleteProperty = (propertyId: number) => {
            void deleteProperty(propertyId);
            setDeletePropertyModalStatus(false);
        };

        const handleDeletePropertyModalClose = () => {
            setDeletePropertyModalStatus(false);
        };

        return (
            <>
                <DeletePropertyModal
                    showModal={deletePropertyModalOpen}
                    deleteInfo={deleteInfo}
                    isDeleteInfoPending={isDeleteInfoPending}
                    onConfirm={handleDeleteProperty}
                    onCancel={handleDeletePropertyModalClose}
                />
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            ml: "24px",
                            mt: "20px",
                            cursor: "pointer",
                            alignItems: "center",
                            gap: "4px",
                            mb: "20px",
                            width: "120px",
                        }}
                        onClick={handleBackButtonClicked}
                    >
                        <IconBox>
                            <Icon icon="chevron-left" size="xs" />
                        </IconBox>
                        <Typography variant="body2">Back to project</Typography>
                    </Box>
                    <Typography
                        variant="h1"
                        sx={{
                            px: "24px",
                            pb: "16px",
                        }}
                    >
                        Project Details
                    </Typography>
                    <Box
                        sx={{
                            borderBottom: "1px solid var(--proda-white-130)",
                        }}
                    >
                        {tabs.map(({ value, label }) => (
                            <Box
                                key={value}
                                component="button"
                                onClick={() => setActiveDetailsTab(value)}
                                data-testid={testIds.tabButton}
                                sx={{
                                    p: "0.8rem 0",
                                    border: "none",
                                    borderBottom: "2px solid",
                                    ml: "1.5rem",
                                    borderColor:
                                        activeDetailsTab === value
                                            ? "var(--primary-color)"
                                            : "transparent",
                                    backgroundColor: "transparent",
                                    cursor: "pointer",
                                    color:
                                        activeDetailsTab === value
                                            ? "var(--primary-color)"
                                            : "inherit",
                                    fontWeight:
                                        activeDetailsTab === value
                                            ? "bold"
                                            : "normal",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        color: "var(--primary-color)",
                                    },
                                }}
                            >
                                {label}
                            </Box>
                        ))}
                    </Box>
                    <Box
                        sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            flexGrow: 1,
                        }}
                    >
                        <Box
                            sx={{
                                width: "100%",
                                flexGrow: 1,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                            }}
                        >
                            {activeDetailsTab === "properties" &&
                                !editingProperty && (
                                    <PropertyList
                                        addPropertyIsPending={
                                            isAddPropertyPending
                                        }
                                        properties={
                                            currentDeal.properties ?? []
                                        }
                                        newProperty={newProperty}
                                        editProperty={propertyIndex => {
                                            const prop =
                                                currentDeal.properties[
                                                    propertyIndex
                                                ];
                                            if (!prop) return;
                                            editProperty(prop);
                                        }}
                                        onDeleteClicked={propertyIndex => {
                                            const prop =
                                                currentDeal.properties[
                                                    propertyIndex
                                                ];
                                            // Delete/info target the Excelsior id;
                                            // propertyId is 0 for multiprop props.
                                            if (
                                                prop?.excelsiorPropertyId ==
                                                null
                                            )
                                                return;
                                            handleDeletePropertyModalOpen(
                                                prop.excelsiorPropertyId,
                                            );
                                        }}
                                    />
                                )}
                            {activeDetailsTab === "properties" &&
                                editingProperty && (
                                    <PropertyForm<
                                        NewProperty | MultipropPropertyInfo
                                    >
                                        property={currentProperty}
                                        databaseId={databaseId}
                                        onSubmit={async (
                                            data:
                                                | NewProperty
                                                | MultipropPropertyInfo,
                                        ) => {
                                            const isExistingProperty =
                                                "propertyId" in
                                                    currentProperty &&
                                                currentProperty.propertyId !==
                                                    undefined &&
                                                currentProperty.propertyId !==
                                                    null;
                                            if (isExistingProperty) {
                                                await updateProperty(
                                                    data as MultipropPropertyInfo,
                                                );
                                            } else {
                                                await addProperty(
                                                    data as NewProperty,
                                                );
                                            }
                                            toggleEditingProperty();
                                        }}
                                        onBack={toggleEditingProperty}
                                        mutateNewProperty={mutateNewProperty}
                                        setPropertyAddressCheck={
                                            setPropertyAddressCheck
                                        }
                                        setPropertyNameCheck={
                                            setPropertyNameCheck
                                        }
                                        propertyAddressCheck={
                                            propertyAddressCheck
                                        }
                                        configGoogleMapsState={
                                            configGoogleMapsState
                                        }
                                        getCurrentDate={getCurrentDate}
                                    />
                                )}
                            {activeDetailsTab === "description" && (
                                <EditDealInfoForm
                                    availableCoAs={availableCoAs ?? []}
                                    availableCoAsStatus={availableCoAsStatus}
                                    disableCoAsDropdown={
                                        hasCoaLockingExtractions
                                    }
                                    disabledCoAsDropdownTooltip="Chart of Accounts cannot be changed while this Project has Operating Statement extractions"
                                    initialData={currentDeal}
                                    databaseId={databaseId}
                                    onSubmit={async data => {
                                        await updateDeal({
                                            ...data,
                                            projectId,
                                            databaseId,
                                        });
                                    }}
                                    setDealNameCheck={setDealNameCheck}
                                />
                            )}
                        </Box>
                    </Box>
                </Box>
            </>
        );
    };

    return ViewDealDetails;
};
